import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/core/store';
import { hideNotification } from '@/core/notify/notificationSlice';
import { Icon } from './Icon';
import './CustomNotifications.scss';

/** How long a toast stays: 5 s from the moment it appears; a new toast replaces it and restarts the timer. */
export const TOAST_DURATION_MS = 5000;

/** The single toast slot (Angular `app-custom-notifications`). Only the message is shown; the header is ignored. */
export function CustomNotifications() {
  const notification = useSelector((state: RootState) => state.notification.current);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => dispatch(hideNotification()), TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [notification, dispatch]);

  const isSuccess = notification?.type === 'success';
  const isError = notification?.type === 'error';
  const isInfo = notification?.type === 'info';
  const isWarning = notification?.type === 'warning';

  return (
    <div className="custom-notifications-host">
      {notification && (
        <div
          className={[
            'alert',
            isSuccess && 'alert-success',
            (isError || isWarning) && 'alert-danger',
            isInfo && 'alert-info',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="d-flex align-items-center">
            {isSuccess && <Icon name="check_circle" className="check-icon text-success-100" />}
            {(isError || isWarning) && <Icon name="error" className="error-icon text-danger-100" />}
            {isInfo && <Icon name="info" className="check-icon text-blue-200" />}
            <div>
              <p
                className={[
                  'mb-0',
                  isSuccess && 'text-success',
                  (isError || isWarning) && 'text-danger-100',
                  isInfo && 'text-blue-200',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {notification.message}
              </p>
            </div>
          </div>
          <div className="alert-close" onClick={() => dispatch(hideNotification())}>
            <Icon name="close" />
          </div>
        </div>
      )}
    </div>
  );
}
